import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { tourApprovalsService } from "api/services/tour-approvals.service";
import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import Modal from "components/UI/Modal";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";

import type { TourApproval, TourApprovalStatus } from "api/types/tour-approval.types";
import "pages/AdminPage.scss";

// ─────────────────────────────────────────────
// 상수
// ─────────────────────────────────────────────
const TABS: TabItem[] = [
  { key: "all", label: "전체" },
  { key: "pending", label: "대기" },
  { key: "approved", label: "승인" },
  { key: "rejected", label: "거절" },
];

const formatDate = (v: string | null) => (v ? v.slice(0, 10) : "-");

const formatPrice = (v: number | null) => (v != null ? `${v.toLocaleString()}원` : "-");

const getTourPeriod = (start: string | null, end: string | null) => {
  if (!start && !end) return "-";
  if (!start) return `~ ${formatDate(end)}`;
  if (!end) return `${formatDate(start)} ~`;
  return `${formatDate(start)} ~ ${formatDate(end)}`;
};

// ─────────────────────────────────────────────
// 컴포넌트
// ─────────────────────────────────────────────
const TourApprovalList: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TourApprovalStatus>("pending");
  const [items, setItems] = useState<TourApproval[]>([]);
  const [loading, setLoading] = useState(false);

  // 날짜 필터 (클라이언트 필터링)
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // 승인/거절 확인 모달
  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    type: "approve" | "reject" | null;
    item: TourApproval | null;
  }>({ open: false, type: null, item: null });
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // ─────────────────────────────────────────
  // 데이터 조회
  // ─────────────────────────────────────────
  const fetchList = async (status: TourApprovalStatus) => {
    setLoading(true);
    try {
      const res = await tourApprovalsService.list({ status, page_size: 100 });
      setItems(res.data.results ?? []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ─────────────────────────────────────────
  // 상태 판별
  // ─────────────────────────────────────────
  const getStatus = (item: TourApproval): "approved" | "rejected" | "pending" => {
    if (item.is_approved) return "approved";
    if (item.approved_at) return "rejected";
    return "pending";
  };

  // ─────────────────────────────────────────
  // 필터링 (클라이언트)
  // ─────────────────────────────────────────
  const filteredItems = items.filter((item) => {
    // 대기 탭에서는 거절된 내역은 노출하지 않는다.
    if (activeTab === "pending" && getStatus(item) === "rejected") return false;

    const target = item.start_date ?? item.created_at;
    if (dateFrom && target < dateFrom) return false;
    if (dateTo && target > dateTo + "T99") return false;
    return true;
  });

  // ─────────────────────────────────────────
  // 승인 / 거절 처리
  // ─────────────────────────────────────────
  const openConfirm = (type: "approve" | "reject", item: TourApproval) => {
    setRejectReason("");
    setConfirmModal({ open: true, type, item });
  };

  const closeConfirm = () => setConfirmModal({ open: false, type: null, item: null });

  const handleConfirm = async () => {
    if (!confirmModal.item || !confirmModal.type) return;
    setActionLoading(true);
    try {
      await tourApprovalsService.action(confirmModal.item.id, {
        action: confirmModal.type,
        ...(confirmModal.type === "reject" ? { reject_reason: rejectReason } : {}),
      });
      closeConfirm();
      fetchList(activeTab);
    } catch {
      // ignore
    } finally {
      setActionLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // 상태 뱃지
  // ─────────────────────────────────────────
  const statusBadge = (item: TourApproval) => {
    const status = getStatus(item);
    if (status === "approved") return <Badge variant="active">승인</Badge>;
    if (status === "rejected") return <Badge variant="rejected">거절</Badge>;
    return <Badge variant="pending">대기</Badge>;
  };

  // ─────────────────────────────────────────
  // 테이블 컬럼 정의
  // ─────────────────────────────────────────
  // 관리(승인/거절) 버튼은 대기/전체 탭에서만 노출 (승인·거절 탭에서는 제거)
  const showActions = activeTab === "pending" || activeTab === "all";

  const columns: Column<TourApproval>[] = [
    {
      key: "name",
      title: "투어 이름",
      width: "280px",
      render: (v) => <span style={{ fontWeight: 500, color: "#18181B" }}>{String(v)}</span>,
    },
    {
      key: "start_date",
      title: "투어 기간",
      width: "180px",
      render: (_, row) => getTourPeriod(row.start_date, row.end_date),
    },
    {
      key: "guide_names",
      title: "인솔강사",
      width: "140px",
      render: (v) => {
        const names = v as string[];
        return names.length > 0 ? names.join(", ") : "-";
      },
    },
    {
      key: "location",
      title: "투어 장소",
      width: "140px",
    },
    {
      key: "price",
      title: "예약금",
      width: "100px",
      render: (v) => formatPrice(v as number | null),
    },
    {
      key: "max_participants",
      title: "정원",
      width: "70px",
      render: (v) => `${v}명`,
    },
    {
      key: "is_approved",
      title: "상태",
      width: "80px",
      render: (_, row) => statusBadge(row),
    },
    ...(showActions
      ? [
          {
            key: "actions",
            title: "관리",
            width: "150px",
            render: (_: unknown, row: TourApproval) => (
              <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="primary" disabled={row.is_approved} onClick={() => openConfirm("approve", row)}>
                  승인
                </Button>
                <Button size="sm" variant="danger" disabled={row.is_approved} onClick={() => openConfirm("reject", row)}>
                  거절
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  // ─────────────────────────────────────────
  // 렌더
  // ─────────────────────────────────────────
  return (
    <div className="admin-page">
      <ContentHeader title="투어 개설 신청 관리" description="투어 개설 신청을 검토하고 승인 또는 거절합니다." />

      <div className="admin-page__card">
        <Tabs items={TABS} activeKey={activeTab} onChange={(k) => setActiveTab(k as TourApprovalStatus)} />

        {/* 날짜 필터 */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px 4px" }}>
          <span style={{ fontSize: 13, color: "#71717A", whiteSpace: "nowrap" }}>투어 시작일</span>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              height: 32,
              padding: "0 8px",
              border: "1px solid #E4E4E7",
              borderRadius: 6,
              fontSize: 13,
              outline: "none",
              color: "#18181B",
            }}
          />
          <span style={{ fontSize: 13, color: "#71717A" }}>~</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              height: 32,
              padding: "0 8px",
              border: "1px solid #E4E4E7",
              borderRadius: 6,
              fontSize: 13,
              outline: "none",
              color: "#18181B",
            }}
          />
          {(dateFrom || dateTo) && (
            <button
              onClick={() => {
                setDateFrom("");
                setDateTo("");
              }}
              style={{
                background: "none",
                border: "1px solid #E4E4E7",
                borderRadius: 6,
                padding: "0 10px",
                height: 32,
                fontSize: 12,
                color: "#71717A",
                cursor: "pointer",
              }}
            >
              초기화
            </button>
          )}
        </div>

        {loading ? <div style={{ padding: 40, textAlign: "center", color: "#71717A" }}>불러오는 중...</div> : <Table columns={columns} data={filteredItems} rowKey="id" onRowClick={(row) => navigate(`/tour-edit/${row.id}?source=approval`)} />}
      </div>

      {/* 승인/거절 확인 모달 */}
      <Modal
        open={confirmModal.open}
        title={confirmModal.type === "approve" ? "투어 승인" : "투어 거절"}
        width="440px"
        onClose={closeConfirm}
        footer={
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="outline" size="sm" onClick={closeConfirm}>
              취소
            </Button>
            <Button variant={confirmModal.type === "approve" ? "primary" : "danger"} size="sm" onClick={handleConfirm} disabled={actionLoading}>
              {actionLoading ? "처리 중..." : confirmModal.type === "approve" ? "승인" : "거절"}
            </Button>
          </div>
        }
      >
        <p
          style={{
            fontSize: 14,
            color: "#3F3F46",
            lineHeight: 1.6,
            marginBottom: confirmModal.type === "reject" ? 16 : 0,
          }}
        >
          <strong>"{confirmModal.item?.name}"</strong> 투어를 {confirmModal.type === "approve" ? "승인" : "거절"}하시겠습니까?
        </p>
        {confirmModal.type === "reject" && (
          <div>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: "#3F3F46",
                display: "block",
                marginBottom: 6,
              }}
            >
              거절 사유 <span style={{ color: "#71717A", fontWeight: 400 }}>(선택)</span>
            </label>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="거절 사유를 입력하세요."
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                border: "1px solid #E4E4E7",
                borderRadius: 8,
                fontSize: 13,
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TourApprovalList;
